import {
  FolderOpenIcon,
  ClockIcon,
  HelpCircleIcon,
  LibraryIcon,
  ShuffleIcon,
  RotateCcwIcon,
  MapIcon,
  ClipboardListIcon,
  RepeatIcon,
  NotebookPenIcon,
  ZapIcon,
  FileTextIcon,
  PlayCircleIcon,
  DownloadIcon,
  PenLineIcon,
  SmartphoneIcon,
  type LucideIcon,
} from "lucide-react";

export type IconItem = { icon: LucideIcon; text: string };
export type TitledIconItem = { icon: LucideIcon; title: string; text: string };

export const scatteredResources: IconItem[] = [
  { icon: FileTextIcon, text: "A chart here." },
  { icon: PlayCircleIcon, text: "A video there." },
  { icon: DownloadIcon, text: "A PDF you downloaded months ago." },
  { icon: PenLineIcon, text: "Notes from a course." },
  { icon: SmartphoneIcon, text: "Screenshots on your phone." },
];

export const practiceQuestions = [
  "Where do I start?",
  "What should I review?",
  "What comes next?",
  "How do I organize my practice session?",
];

export const beforeItems = [
  "You search through different resources before you begin.",
  "You have several charts but aren't sure which one to reference first.",
  "You constantly go back to your notes to remember basic concepts.",
  "You understand some of the theory but struggle to turn it into a practical sequence.",
  "You spend more time searching than practicing.",
  "You have plenty of resources but no clear structure.",
  "Before you begin, you're still wondering what you should do next.",
];

export const afterItems = [
  "You have a visual flow for organizing your practice.",
  "You know what to reference at each stage.",
  "You can follow a structured step-by-step process.",
  "You have checklists to prepare and organize your practice.",
  "You can record what you practice.",
  "You can track your progress.",
  "Your essential practice tools are organized in one system.",
];

export const painPoints: IconItem[] = [
  { icon: FolderOpenIcon, text: "You have several reflexology resources, but when you're ready to practice, you're not sure which one to open first." },
  { icon: ClockIcon, text: "You spend too much time searching through notes, videos, charts, or PDFs before you can begin." },
  { icon: HelpCircleIcon, text: "You understand the basics, but you still feel unsure about how to organize a practice session." },
  { icon: LibraryIcon, text: "You've collected reflexology information for months, but everything is scattered across different files and resources." },
  { icon: ShuffleIcon, text: "Every time you want to practice, you end up improvising because you don't have a clear visual sequence to follow." },
  { icon: RotateCcwIcon, text: "Whenever you want to review something, you feel like you have to go back and study everything again." },
];

export const practicalTools: TitledIconItem[] = [
  { icon: MapIcon, title: "Visual Session Maps", text: "Visual maps that make it easier to see where you are within the practice flow." },
  { icon: ClipboardListIcon, title: "Practice Checklists", text: "Simple checklists for preparing, practicing, closing, and recording each session." },
  { icon: RepeatIcon, title: "Step-by-Step Sequences", text: "Visual frameworks showing what comes before and after each stage." },
  { icon: NotebookPenIcon, title: "Practice Worksheets", text: "Tools for recording observations, questions, notes, and progress." },
  { icon: ZapIcon, title: "Quick-Reference Guides", text: "Compact resources you can access from your phone whenever you need a quick reminder." },
];

export const guaranteeItems = [
  "Explore all 6 volumes.",
  "Review the visual systems.",
  "Use the practice checklists.",
  "Open the practice flows.",
  "Try the worksheets.",
  "Explore all 5 bonuses.",
];

export const accumulatedItems = ["Maps", "Charts", "Videos", "PDFs", "Notes", "Courses", "Tips", "Saved posts"];

export const finalIncluded = [
  "6 Premium Volumes",
  "5 Exclusive Bonuses",
  "Visual Session System",
  "Practice Flows",
  "Checklists",
  "Practice Worksheets",
  "Quick-Reference Guides",
];

export const faqs = [
  { q: "How will I receive Reflexology Session Blueprint™?", a: "Immediately after completing your purchase, you'll receive instructions to access and download all 6 volumes and 5 bonuses in digital format." },
  { q: "Can I use it on my phone?", a: "Yes. The system includes resources designed for easy reference from your phone, tablet, or computer." },
  { q: "Do I need previous reflexology experience?", a: "The system is primarily designed as an organization, reference, and practice tool for people who are learning or practicing reflexology. It does not replace professional training where professional training is required." },
  { q: "Are foot and hand references included?", a: "Yes. The system includes visual reference materials for both feet and hands." },
  { q: "Are the practice protocols medical?", a: "No. The materials are educational wellness and practice-organization resources. They are not intended to diagnose, treat, cure, or prevent any disease and should not replace professional medical care." },
  { q: "Is Reflexology Session Blueprint™ a video course?", a: "No. It's a visual digital reference, organization, and practice system." },
  { q: "Is it a one-time payment?", a: "Yes. You pay just $27 once. The price is converted automatically to your local currency at checkout." },
  { q: "Do I get lifetime access?", a: "Yes. You'll receive lifetime access to the purchased materials, subject to the applicable purchase terms." },
  { q: "Can I print the materials?", a: "Yes. Many of the resources are designed to work both digitally and in print." },
  { q: "What if I don't like it?", a: "You have a 7-day guarantee. You can explore the system and, if you decide it's not right for you, request a refund according to the purchase terms." },
];
